/**
 * Reply drawer — a dedicated slide-in panel (like the KC2 reference) that hosts
 * the full reply form: channel tabs, To, Category/Sub Category, Quick Reply,
 * a Public/Private toggle, a rich-text editor and attachments.
 */
import type { JSX } from 'react'
import { Reply } from 'lucide-react'
import type { ConversationDetailResponse, Message } from '../../../modules/conversations/types.js'
import { getChannelConfig } from '../../../modules/conversations/channelConfig.js'
import ActionDrawer from './ActionDrawer.js'
import ReplyForm from './ReplyForm.js'
import { formatCustomerName } from '../../../utils/formatCustomerName.js'

type Conversation = ConversationDetailResponse['conversation']

export interface ReplyDrawerProps {
  open: boolean
  onClose: () => void
  conversation: Conversation
  currentAgentId: string | null
  initialMode: 'reply' | 'note'
  canEdit: boolean
  onPatch: (patch: Record<string, unknown>) => Promise<boolean>
  appendMessage: (m: Message) => void
  replaceMessage: (oldId: string, m: Message) => void
  markMessageFailed: (id: string, error: string) => void
}

export default function ReplyDrawer(props: ReplyDrawerProps): JSX.Element | null {
  const { conversation } = props
  const channel = getChannelConfig(conversation.channelOrigin)
  return (
    <ActionDrawer
      open={props.open}
      onClose={props.onClose}
      title={props.initialMode === 'note' ? 'Add internal note' : 'Reply'}
      subtitle={`${channel.label} · ${formatCustomerName(conversation.displayName, conversation.primaryHandle, conversation.channelOrigin, conversation.fbAccountHolderName)}`}
      icon={<Reply className="h-4 w-4" />}
      info={{
        lead:
          props.initialMode === 'note'
            ? 'Records an internal note on this conversation. Nothing is sent to the customer.'
            : 'Sends a reply to the customer on this conversation\u2019s channel.',
        points: [
          { term: 'Public vs private', detail: 'Public goes to the requester; private stays on the internal timeline for agents only.' },
          { term: 'Channel', detail: 'Determines how the message leaves — email, SMS, WhatsApp and social each have their own limits.' },
          { term: 'Quick Reply', detail: 'Drops a saved template into the editor, which you can then edit before sending.' },
          { term: 'Attachments', detail: 'Uploaded first, then sent with the message. Channel size limits still apply.' },
        ],
      }}
      width={1100}
      padded={false}
    >
      <ReplyForm
        key={`${conversation.id}-${props.initialMode}-${String(props.open)}`}
        conversation={conversation}
        currentAgentId={props.currentAgentId}
        initialMode={props.initialMode}
        canEdit={props.canEdit}
        onPatch={props.onPatch}
        onSent={props.onClose}
        appendMessage={props.appendMessage}
        replaceMessage={props.replaceMessage}
        markMessageFailed={props.markMessageFailed}
      />
    </ActionDrawer>
  )
}
